import { Component, EventEmitter, OnInit } from '@angular/core';
import { DomainService } from '../domain.service';
import { Domain } from '../domain';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-domain-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class DomainListComponent implements OnInit {
  public domain: Domain;

  /**
   * A list of all the domains currently loaded.
   *
   * @type {Domain[]}
   */
  public domains: Domain[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of domains.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of domains that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of domains that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a domain has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public domainDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {DomainService} domainService
   * @param {NotificationService} notificationService
   */
  constructor(
    private domainService: DomainService,
    private notificationService: NotificationService,
  ) {
    this.domainService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.domainService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.domainService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.domainService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the domain list.
   */
  public ngOnInit(): void {
    this.loadDomains(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * domain list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadDomains(pageNumber);
  }

  /**
   * Loads a list of domains using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadDomains(page?: number, limit?: number): void {
    const listDomains = this.domainService
      .listDomains(page, limit)
      .subscribe((domains: Domain[]) => {
        this.domains = domains;
        listDomains.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate domain ID
   * to delete the domain.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.domainService.deleteDomain(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Domain Deleted`,
          `Domain with ID ${id} was deleted.`,
          5,
        );
        this.domainDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Domain Not Deleted`,
          err.error.message,
          10,
        );
        this.domainDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
